import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Calendar, User, BookOpen, Clock } from 'lucide-react';

// Blog data extracted from the provided content
const blogsData = [
  {
    id: 1,
    title: 'Corporate Event Planning: 5 Secrets to a Successful Product Launch',
    excerpt: 'A product launch is more than just a reveal; it’s a strategic milestone. Learn how professional event services can transform your next corporate event into a high-impact brand experience that drives ROI.',
    author: 'Zaid Masood',
    date: 'April 10, 2026',
    readTime: '5 min read',
    category: 'Corporate',
    image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400',
    color: 'from-blue-400 to-cyan-400',
  },
  {
    id: 2,
    title: 'The Ultimate Wedding Planning Checklist: From "Yes" to "I Do"',
    excerpt: 'Starting your journey to "I Do"? Our comprehensive wedding checklist ensures no detail is missed. Discover how Jashn simplifies the path to a breathtaking celebration.',
    author: 'Ayesha Ejaz',
    date: 'April 5, 2026',
    readTime: '5 min read',
    category: 'Wedding',
    image: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=400',
    color: 'from-pink-400 to-rose-400',
  },
  {
    id: 3,
    title: 'Beyond the Cake: 5 Trending Birthday Party Themes for 2026',
    excerpt: 'Ready to celebrate in style? Discover the top 5 themed birthday party trends for 2026 with Jashn. From immersive "Enchanted Forests" to luxury adult lounges, we make every birthday legendary.',
    author: 'Hassaan Mansur',
    date: 'March 28, 2026',
    readTime: '5 min read',
    category: 'Birthday',
    image: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=400',
    color: 'from-yellow-400 to-orange-400',
  },
  {
    id: 4,
    title: 'The Art of the Escape: Why You Need a Travel Planner for Your Next Group Trip',
    excerpt: 'Whether you are organizing a high-stakes corporate retreat or a multi-generational family vacation, the logistics can quickly overshadow the joy of the journey. Here is why a travel event planner is the essential ingredient for your next successful group escape.',
    author: 'Heer Harish',
    date: 'March 20, 2026',
    readTime: '5 min read',
    category: 'Travel',
    image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400',
    color: 'from-green-400 to-emerald-400',
  },
  {
    id: 5,
    title: 'The Ultimate 2026 Guide to Using a Wedding Planner Checklist & Tick List',
    excerpt: 'From hiring a professional wedding planner to managing your own DIY details, having a structured approach is non-negotiable. In this guide, we’ll explore how to navigate your planning phase using the best tools available.',
    author: 'Aisha Siddiqa',
    date: 'March 15, 2026',
    readTime: '5 min read',
    category: 'Wedding',
    image: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=400',
    color: 'from-pink-400 to-rose-400',
  },
  {
    id: 6,
    title: '7 High-Impact Team Building Activities for Work That Actually Boost Productivity',
    excerpt: 'In today’s fast-paced corporate world, teamwork is no longer optional—it’s essential. That’s where structured team building activities for work become powerful tools. Here are seven impactful formats that truly make a difference.',
    author: 'Junaid Asrar',
    date: 'March 8, 2026',
    readTime: '4 min read',
    category: 'Corporate',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400',
    color: 'from-blue-400 to-cyan-400',
  },
  {
    id: 7,
    title: 'The Modern Couple’s Guide to Sustainable Micro-Weddings & Wedding Website Builders',
    excerpt: 'Weddings in 2026 are becoming more intentional. Couples are prioritizing sustainability, intimacy, and digital organization over extravagant excess. Here’s how to combine both for a seamless experience.',
    author: 'Abdullah Ashfaq',
    date: 'March 1, 2026',
    readTime: '4 min read',
    category: 'Sustainability',
    image: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=400',
    color: 'from-purple-400 to-pink-400',
  },
];

const Allblogs = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-pink-50">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-28 pb-16 px-4 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-200 rounded-full -translate-x-1/2 -translate-y-1/2 opacity-20"></div>
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-pink-200 rounded-full translate-x-1/3 translate-y-1/3 opacity-20"></div>

        <div className="container mx-auto text-center relative z-10">
          <div className="inline-flex items-center justify-center p-4 bg-gradient-to-r from-yellow-100 to-pink-100 rounded-2xl mb-6">
            <BookOpen className="h-12 w-12 text-yellow-500" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-yellow-500 to-pink-500 bg-clip-text text-transparent">
            Jashn Blog
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Insights, inspiration, and expert advice to help you plan celebrations that matter.
          </p>
        </div>
      </section>

      {/* Blog Cards Grid */}
      <section className="pb-20 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogsData.map((blog) => (
              <Link
                key={blog.id}
                to={`/blog/${blog.id}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
              >
                {/* Image with gradient overlay */}
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={blog.image}
                    alt={blog.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    onError={(e) => {
                      // Fallback gradient with category initial
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `
                        <div class="w-full h-full flex items-center justify-center bg-gradient-to-r ${blog.color}">
                          <span class="text-6xl font-bold text-white">${blog.category[0]}</span>
                        </div>
                      `;
                    }}
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`px-4 py-1.5 bg-white/90 backdrop-blur-sm text-sm font-semibold rounded-full shadow-sm
                      ${blog.category === 'Corporate' ? 'text-blue-600' : 
                        blog.category === 'Wedding' ? 'text-pink-600' : 
                        blog.category === 'Birthday' ? 'text-orange-600' : 
                        blog.category === 'Travel' ? 'text-emerald-600' : 
                        'text-purple-600'}`}>
                      {blog.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3 text-gray-800 group-hover:text-yellow-600 transition-colors line-clamp-2">
                    {blog.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3 text-sm leading-relaxed">
                    {blog.excerpt}
                  </p>

                  {/* Meta info */}
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                    <div className="flex items-center space-x-2">
                      <User className="h-3.5 w-3.5" />
                      <span>{blog.author}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-3.5 w-3.5" />
                      <span>{blog.date}</span>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="pt-4 border-t border-yellow-100 flex items-center justify-between">
                    <div className="flex items-center space-x-1 text-gray-500">
                      <Clock className="h-4 w-4" />
                      <span className="text-sm">{blog.readTime}</span>
                    </div>
                    <span className="text-yellow-600 font-medium group-hover:translate-x-2 transition-transform inline-flex items-center">
                      Read Article
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                </div>

                {/* Decorative corner accent */}
                <div className={`absolute top-0 right-0 w-16 h-16 bg-gradient-to-br ${blog.color} opacity-0 group-hover:opacity-10 rounded-bl-2xl transition-opacity duration-500`}></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Allblogs;